/**
 * Applies easing and timing constraints to child animations.
 *
 * Warps the parent fraction using a timing function and timing range,
 * then passes the warped fraction to all children.
 */

import { AnimationsInParallel, PostUpdatesFunc, IPaneState, PaneStatePostUpdateFunc } from './AnimationsInParallel';
import { FractionWarperTiming } from './FractionWarperTiming';

function clamp(value: number, min: number, max: number): number {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
}

function calculateClampedFraction(inFraction: number, start: number, end: number): number {
  if (start === end) {
    return inFraction <= start ? 0 : 1;
  } else if (end > start) {
    const fraction = (inFraction - start) / (end - start);
    return clamp(fraction, 0, 1);
  } else {
    // Reversed timing: end < start
    const fraction = (1 - inFraction - end) / (start - end);
    return clamp(fraction, 0, 1);
  }
}

export class FractionWarperAnimation extends AnimationsInParallel {
  private timing: FractionWarperTiming;

  constructor();
  constructor(postUpdatesFunc: PostUpdatesFunc);
  constructor(paneState: IPaneState, paneStatePostUpdateFunc: PaneStatePostUpdateFunc);

  constructor(arg1?: PostUpdatesFunc | IPaneState, arg2?: PaneStatePostUpdateFunc) {
    if (arg1 !== undefined && arg2 !== undefined) {
      super(arg1 as IPaneState, arg2);
    } else if (arg1 !== undefined && typeof arg1 === 'function') {
      super(arg1 as PostUpdatesFunc);
    } else {
      super();
    }

    this.timing = new FractionWarperTiming();
  }

  public setTiming(timing: FractionWarperTiming): void {
    this.timing = timing;
  }

  public getTiming(): FractionWarperTiming {
    return this.timing;
  }

  public update(): void {
    if (this.timing === null) {
      return;
    }

    const clampedFraction = calculateClampedFraction(this.getFraction(), this.timing.startFraction, this.timing.endFraction);

    let warpedFraction: number;
    if (this.timing.warpingFunction !== null) {
      warpedFraction = this.timing.warpingFunction(clampedFraction);
    } else {
      warpedFraction = clampedFraction;
    }

    for (const child of this.children) {
      child.setFraction(warpedFraction);
    }

    this.callPostUpdateCallbacks();
  }

  private callPostUpdateCallbacks(): void {
    if (this.postUpdatesFunc) {
      this.postUpdatesFunc();
    }

    if (this.paneStatePostUpdateFunc && this.paneState) {
      this.paneStatePostUpdateFunc(this.paneState);
    }
  }
}
